import React, { useContext, useEffect, useRef, useState } from "react";
import { Rows } from "./Row";
import {
    CardTableRows,
    CardTableSelectAllControl,
    CardTableShell,
    CardTableToolbar,
    FlexContainer,
    LottieLoadingIndicator,
    Toast
} from "src/components";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import { ActionButton, TextLabel } from "../../../components/style";
import CsvIcon from "src/img/emailCampaigns/csv.svg";

import { useTableHooks } from "../../../hooks/useTableHooks";
import HeaderCard from "src/scenes/EmailCampaigns/components/HeaderCard";
import { EmailCampaignsContext } from "src/scenes/EmailCampaigns/EmailCampaignsContext";
import { CsvFileHooks } from "src/scenes/EmailCampaigns/hooks/csvFileHooks";
import AddSingleContactForm from "src/scenes/EmailCampaigns/components/audience/AddSingleContactForm";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import { PageLoadingContainer } from "src/components/PageLoadingContainer";
import { getContacts, updateContacts } from "src/scenes/EmailCampaigns/api/contactsApi";
import { UserContext } from "src/scenes/App/UserContext";
import { CONTACT_ADDING_TYPE } from "src/scenes/EmailCampaigns/constants";

export const UpdateContactsTable = (props) => {
    const { countryCode } = useContext(UserContext);
    const { selectedAudience, setIsUpdatingContacts, setSelectedAction } = props;
    const { uploadedCsvData, setUploadedCsvData, initialFetchSettings } = useContext(EmailCampaignsContext);
    const [contactsIsFetching, setContactsIsFetching] = useState(false);
    const [contactsList, setContactsList] = useState([]);
    const [newContactList, setNewContactList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);
    const [showAddSingleContact, setShowAddSingleContact] = useState(false);
    const [contactFormData, setContactFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        reference: "",
    });
    const [manuallyAddedContacts, setManuallyAddedContacts] = useState([]);

    const getAllContacts = async () => {
        setContactsIsFetching(true);
        try {
            const response = await getContacts(selectedAudience.uuid);
            if (response) setContactsList(response.contacts);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error occurred while fetching contacts.");
        } finally {
            setContactsIsFetching(false);
        }
    };

    useEffect(() => {
        getAllContacts();
    }, []);

    const onSaveSingleContact = () => {
        const exists = newContactList.some((contact) => contact.email === contactFormData.email);
        if (exists) return Toast.error("Contact with this email already exist.");
        setNewContactList((prev) => [...prev, { ...contactFormData, type: CONTACT_ADDING_TYPE.MANUALLY_ADDED }]);
        setManuallyAddedContacts((prev) => [...prev, { ...contactFormData, type: CONTACT_ADDING_TYPE.MANUALLY_ADDED }]);
        setShowAddSingleContact(!showAddSingleContact);
        setContactFormData({
            firstName: "",
            lastName: "",
            email: "",
            mobileNumber: "",
            reference: "",
        });
    };

    useEffect(() => {
        setNewContactList((prev) => [...contactsList, ...uploadedCsvData, ...manuallyAddedContacts]);
    }, [contactsList, uploadedCsvData, manuallyAddedContacts]);

    const SortableColumns = {
        select: 0,
        firstName: 1,
        lastName: 2,
        email: 3,
        mobile: 4,
        reference: 5,
    };

    const {
        tableData,
        selectedItem,
        selectAllItem,
        handleMainCheckbox,
        toggleItemSelection,
        isItemSelected,
        handleSortColumn,
        handleDeleteSelectedItems,
        updateTableField,
        validateTable,
        tableErrors
    } = useTableHooks(
        newContactList,
        SortableColumns,
        contactsList,
        setContactsList,
        uploadedCsvData,
        setUploadedCsvData,
        manuallyAddedContacts,
        setManuallyAddedContacts
    );

    const isASortableColumn = (index) => index > 0;

    const { handleCsvUpload } = CsvFileHooks(setUploadedCsvData);
    const onImportCsvClick = () => {
        fileInputRef.current.click();
    };

    const toggleAddSingleContact = () => {
        setShowAddSingleContact(!showAddSingleContact);
    };

    const onSubmit = async () => {
        const errors = validateTable();

        if (Object.keys(errors).length > 0) {
            Toast.error("Please fix the highlighted errors");
            return;
        }
        setIsLoading(true);
        try {
            const payload = {
                name: selectedAudience.name,
                contacts: tableData,
            };
            const response = await updateContacts(selectedAudience.uuid, payload);
            if (response) {
                setIsLoading(false);
                setUploadedCsvData([]);
                setIsUpdatingContacts(false);
                initialFetchSettings();
            }

        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoading(false);
        }
    };

    const enableButton = tableData.every(contact => contact.isDuplicate === false);

    if (contactsIsFetching) return <PageLoadingContainer maxWidth={"1112px"} minHeight={"963px"} />;

    return (
        <>
            <CardTableShell maxWidth="1112px" minHeight="963px">
                <CardTableToolbar>
                    <FlexContainer>
                        <TextLabel fontSize="20px" fontWeight="bold">
                            Edit Audience List
                        </TextLabel>
                        <FlexContainer maxWidth="700px">
                            <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                Update your saved audience list. You can modify contact details, add
                                or remove individuals, or re-upload a CSV to replace the current
                                list. When you’re done, save your changes to update the audience.
                            </TextLabel>
                        </FlexContainer>
                    </FlexContainer>
                    <HeaderCard
                        title={selectedAudience.name}
                        subtitle={"Contacts Available"}
                        quantity={uploadedCsvData && newContactList.length}
                    />
                </CardTableToolbar>
                <CardTableToolbar>
                    <CardTableSelectAllControl>
                        <label>
                            <Checkbox
                                checked={selectAllItem}
                                onChange={() => handleMainCheckbox()}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </label>
                    </CardTableSelectAllControl>
                    <FlexContainer
                        border="1px solid #D8DEEC"
                        borderRadius="7px"
                        width="260px"
                        height="38px"
                        style={{ boxShadow: "0px 3px 6px #0000002" }}
                        direction="row"
                        tDirection="row"
                        mDirection="row"
                    >
                        <FlexContainer
                            width="25px"
                            justifyContent="center"
                            alignItems="center"
                            style={{ marginLeft: "10px" }}
                        >
                            <img src={CsvIcon} style={{ width: "20px" }} alt="Business Logo" />
                        </FlexContainer>
                        <FlexContainer
                            direction="row"
                            tDirection="row"
                            mDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            height="100%"
                            padding="0px 20px 0px 10px"
                            width="100%"
                        >
                            <TextLabel marginBottom="0px" fontSize="14px">
                                New Contacts Detected
                            </TextLabel>
                            <TextLabel marginBottom="0px" color="#612684">
                                {uploadedCsvData?.length}
                            </TextLabel>
                        </FlexContainer>
                    </FlexContainer>
                    <FlexContainer direction="row" tDirection="row" gap="15px">
                        {selectedItem.length > 0 && (
                            <ActionButton
                                type="button"
                                width="100%"
                                height="39px"
                                color="#FF3B3B"
                                border="1px solid #FF3B3B"
                                backgroundColor="#FFF5F5"
                                onClick={() => {
                                    handleDeleteSelectedItems();
                                }}
                            >
                                Delete Contact
                            </ActionButton>
                        )}
                        <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={(e) => {
                                handleCsvUpload(e);
                                e.target.value = ""; // ✅ reset file input
                            }}
                        />
                        <ActionButton
                            type="button"
                            width="100%"
                            height="39px"
                            color="#FFFFFF"
                            backgroundColor="#171A22"
                            border="0px"
                            onClick={onImportCsvClick}
                        >
                            {uploadedCsvData.length > 0 ? "Re-upload CSV" : "Import CSV"}
                        </ActionButton>

                        <ActionButton
                            type="button"
                            width="100%"
                            height="39px"
                            color="#FFFFFF"
                            backgroundColor="#171A22"
                            border="0px"
                            onClick={() => toggleAddSingleContact()}
                        >
                            Add Another Contact
                        </ActionButton>
                    </FlexContainer>
                </CardTableToolbar>
                <CardTableRows maxHeight="44rem" minHeight="36rem">
                    <Rows
                        tableData={tableData}
                        onToggle={(contact) => toggleItemSelection(contact)}
                        isSelected={isItemSelected}
                        isViewingContacts={false}
                        countryCode={countryCode}
                        onChangeField={updateTableField}
                        errors={tableErrors}
                        onSort={handleSortColumn}
                        isSortable={isASortableColumn}
                    />
                </CardTableRows>

                <FlexContainer
                    direction="row"
                    tDirection="row"
                    style={{ justifyContent: "flex-end" }}
                    padding="20px 20px 30px 0px"
                    gap="10px"
                >
                    <FlexContainer width="140px" mWidth="100%">
                        <ActionButton
                            type="button"
                            width="100%"
                            height="39px"
                            color="#000000"
                            backgroundColor="#FFFFFF"
                            onClick={() => {
                                setUploadedCsvData([]);
                                setIsUpdatingContacts();
                                setSelectedAction({});
                            }}
                        >
                            Cancel
                        </ActionButton>
                    </FlexContainer>
                    <FlexContainer width="auto" mWidth="100%">
                        {isLoading ? (
                            <LoadingIndicatorButton>
                                <LottieLoadingIndicator
                                    height="2rem"
                                    width="2rem"
                                    margin="5px"
                                    containerHeight="2rem"
                                />
                            </LoadingIndicatorButton>
                        ) : (
                            <ActionButton
                                type="button"
                                width="100%"
                                height="39px"
                                color="#FFFFFF"
                                backgroundColor={enableButton ? "#171A22" : "#99C2FF"}
                                border="0px"
                                onClick={enableButton ? () => onSubmit() : () => { }}
                            >
                                Save Contacts
                            </ActionButton>
                        )}
                    </FlexContainer>
                </FlexContainer>
            </CardTableShell>
            {showAddSingleContact && (
                <AddSingleContactForm
                    toggleAddSingleContact={toggleAddSingleContact}
                    contactFormData={contactFormData}
                    setContactFormData={setContactFormData}
                    isEditing={true}
                    onSaveSingleContact={onSaveSingleContact}
                />
            )}
        </>
    );
};
